import { zodResolver } from '@hookform/resolvers/zod';
import type { Integration, IntegrationWithCredentials } from '@sea-snag/shared';
import {
  createIntegrationSchema,
  IntegrationProviders,
  type IntegrationProviderType,
  updateIntegrationSchema,
} from '@sea-snag/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sea-snag/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { HTTPError } from 'ky';
import { ArrowDownUp, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

import { RequireOwnerOrAdmin } from '@/components/require-owner-or-admin';

import { ConfirmDialog } from '../../components/confirm-dialog';
import { LoadingState } from '../../components/loading-state';
import { useRequiredOrganization } from '../../hooks/use-required-organization';
import { integrationsApi } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

export const Route = createFileRoute('/$orgPid/integrations')({
  component: GuardedIntegrationsPage,
});

/** Members are redirected to the dashboard; the API refuses them too. */
function GuardedIntegrationsPage() {
  return (
    <RequireOwnerOrAdmin>
      <IntegrationsPage />
    </RequireOwnerOrAdmin>
  );
}

function IntegrationsPage() {
  const { organization, isLoading } = useRequiredOrganization();

  if (isLoading) return <LoadingState />;

  return <IntegrationsContent orgPid={organization.pid} />;
}

function IntegrationsContent({ orgPid }: { orgPid: string }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Integration | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Integration | null>(null);
  const queryClient = useQueryClient();

  const { data: integrations, isLoading } = useQuery({
    queryKey: queryKeys.integrations.list(orgPid),
    queryFn: () => integrationsApi.list(orgPid),
  });

  const deleteMutation = useMutation({
    mutationFn: (integrationId: string) => integrationsApi.remove(orgPid, integrationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.integrations.list(orgPid) });
      toast.success('Integration deleted');
    },
    onError: () => {
      toast.error('Failed to delete integration');
    },
  });

  const validateMutation = useMutation({
    mutationFn: (integrationId: string) => integrationsApi.validate(orgPid, integrationId),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.integrations.list(orgPid) });
      if (data.valid) {
        toast.success('Credentials are valid');
      } else {
        toast.error('Credential validation failed');
      }
    },
    onError: () => {
      toast.error('Failed to validate credentials');
    },
  });

  const syncMutation = useMutation({
    mutationFn: (integrationId: string) => integrationsApi.sync(orgPid, integrationId),
    onSuccess: ({ alreadyQueued }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.integrations.list(orgPid) });
      if (alreadyQueued) toast.info('A sync is already running');
      else toast.success('Sync started');
    },
    onError: () => {
      toast.error('Failed to start sync');
    },
  });

  const statusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      case 'inactive':
        return 'bg-gray-400';
      default:
        return 'bg-yellow-500';
    }
  };

  const providerLabel = (provider: string) => {
    const config = IntegrationProviders[provider as IntegrationProviderType];
    return config?.label ?? provider;
  };

  const formatSyncTime = (lastSyncAt: string | null) => {
    if (!lastSyncAt) return 'Never';
    const date = new Date(lastSyncAt);
    const now = new Date();
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const isToday =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();
    if (isToday) return `Today at ${time}`;

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday =
      date.getFullYear() === yesterday.getFullYear() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getDate() === yesterday.getDate();
    if (isYesterday) return `Yesterday at ${time}`;

    return `${date.toLocaleDateString([], { day: 'numeric', month: 'short' })} at ${time}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Integrations</h1>
          <p className="text-muted-foreground">Connect your charter management platforms</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>Add Integration</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add a new integration</DialogTitle>
              <DialogDescription>
                Connect your MMK or NauSYS account to import yacht data.
              </DialogDescription>
            </DialogHeader>
            <CreateIntegrationForm
              orgPid={orgPid}
              onSuccess={() => {
                setCreateOpen(false);
                void queryClient.invalidateQueries({
                  queryKey: queryKeys.integrations.list(orgPid),
                });
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Connected Platforms</CardTitle>
          <CardDescription>Your active integration providers</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : !integrations || integrations.length === 0 ? (
            <p className="text-muted-foreground">
              No integrations configured yet. Click "Add Integration" to get started.
            </p>
          ) : (
            <div className="space-y-3">
              {integrations.map((integration) => (
                <div
                  key={integration.id}
                  className="flex items-center justify-between rounded-lg border p-4"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`size-2.5 shrink-0 rounded-full ${statusColor(integration.status)}`}
                        title={integration.status}
                      />
                      <p className="font-medium">{integration.name}</p>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Badge variant="outline">{providerLabel(integration.provider)}</Badge>
                      <span>Last sync: {formatSyncTime(integration.lastSyncAt)}</span>
                    </div>
                    {integration.errorMessage && (
                      <p className="text-sm text-destructive">{integration.errorMessage}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => syncMutation.mutate(integration.id)}
                      disabled={syncMutation.isPending || integration.status !== 'active'}
                    >
                      <ArrowDownUp className="mr-1 size-3.5" />
                      Sync
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => validateMutation.mutate(integration.id)}
                      disabled={validateMutation.isPending}
                    >
                      <RefreshCw className="mr-1 size-3.5" />
                      Validate
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setEditTarget(integration)}>
                      <Pencil className="mr-1 size-3.5" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteTarget(integration)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="mr-1 size-3.5" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {editTarget && (
        <EditIntegrationDialog
          orgPid={orgPid}
          integration={editTarget}
          open={!!editTarget}
          onOpenChange={(open) => !open && setEditTarget(null)}
          onSuccess={() => {
            setEditTarget(null);
            void queryClient.invalidateQueries({ queryKey: queryKeys.integrations.list(orgPid) });
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete integration"
        description={`Are you sure you want to delete the "${deleteTarget?.name}" integration? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => {
          if (deleteTarget) {
            deleteMutation.mutate(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
      />
    </div>
  );
}

// --- Create Form ---

function CreateIntegrationForm({ orgPid, onSuccess }: { orgPid: string; onSuccess: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<IntegrationProviderType>('mmk');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof createIntegrationSchema>>({
    resolver: zodResolver(createIntegrationSchema),
    defaultValues: { provider: 'mmk', credentials: {} },
  });

  const providerFields = IntegrationProviders[selectedProvider].fields;

  const onSubmit = async (data: z.input<typeof createIntegrationSchema>) => {
    setError(null);
    try {
      await integrationsApi.create(orgPid, data);
      toast.success('Integration created successfully');
      onSuccess();
    } catch (err) {
      if (err instanceof HTTPError) {
        const body = (await err.response.json().catch(() => ({}))) as { message?: string };
        setError(body.message || 'Failed to create integration');
      } else {
        setError('An unexpected error occurred');
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4"
      aria-label="Create integration form"
    >
      {error && (
        <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="provider">Provider</Label>
        <Select
          value={selectedProvider}
          onValueChange={(v) => {
            const provider = v as IntegrationProviderType;
            setSelectedProvider(provider);
            setValue('provider', provider);
            setValue('credentials', {});
          }}
        >
          <SelectTrigger id="provider">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(IntegrationProviders) as IntegrationProviderType[]).map((key) => (
              <SelectItem key={key} value={key}>
                {IntegrationProviders[key].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.provider && <p className="text-sm text-destructive">{errors.provider.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="integration-name">Name</Label>
        <Input id="integration-name" placeholder="e.g. My MMK Account" {...register('name')} />
        {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
      </div>

      {providerFields.map((field) => (
        <div key={field.key} className="space-y-2">
          <Label htmlFor={`cred-${field.key}`}>{field.label}</Label>
          <Input
            id={`cred-${field.key}`}
            type={field.type}
            placeholder={field.label}
            {...register(`credentials.${field.key}` as const)}
          />
        </div>
      ))}

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? 'Creating...' : 'Create Integration'}
      </Button>
    </form>
  );
}

// --- Edit Dialog ---

function EditIntegrationDialog({
  orgPid,
  integration,
  open,
  onOpenChange,
  onSuccess,
}: {
  orgPid: string;
  integration: Integration;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}) {
  const [error, setError] = useState<string | null>(null);

  const { data: details, isLoading } = useQuery({
    queryKey: queryKeys.integrations.detail(orgPid, integration.id),
    queryFn: () => integrationsApi.get(orgPid, integration.id),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Integration</DialogTitle>
          <DialogDescription>
            Update the name or credentials for this integration.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : details ? (
          <EditIntegrationForm
            orgPid={orgPid}
            details={details}
            error={error}
            setError={setError}
            onSuccess={onSuccess}
          />
        ) : (
          <p className="text-destructive">Failed to load integration details.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditIntegrationForm({
  orgPid,
  details,
  error,
  setError,
  onSuccess,
}: {
  orgPid: string;
  details: IntegrationWithCredentials;
  error: string | null;
  setError: (error: string | null) => void;
  onSuccess: () => void;
}) {
  const providerType = details.provider as IntegrationProviderType;
  const providerFields = IntegrationProviders[providerType].fields;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof updateIntegrationSchema>>({
    resolver: zodResolver(updateIntegrationSchema),
    defaultValues: {
      name: details.name,
      credentials: details.credentials,
    },
  });

  useEffect(() => {
    reset({ name: details.name, credentials: details.credentials });
  }, [details, reset]);

  const onSubmit = async (data: z.input<typeof updateIntegrationSchema>) => {
    setError(null);
    try {
      const payload = data.credentials
        ? { ...data, credentials: { ...details.credentials, ...data.credentials } }
        : data;
      await integrationsApi.update(orgPid, details.id, payload);
      toast.success('Integration updated');
      onSuccess();
    } catch (err) {
      if (err instanceof HTTPError) {
        const body = (await err.response.json().catch(() => ({}))) as { message?: string };
        setError(body.message || 'Failed to update integration');
      } else {
        setError('An unexpected error occurred');
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4"
      aria-label="Edit integration form"
    >
      {error && (
        <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="edit-name">Name</Label>
        <Input id="edit-name" {...register('name')} />
        {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
      </div>

      {providerFields.map((field) => (
        <div key={field.key} className="space-y-2">
          <Label htmlFor={`edit-cred-${field.key}`}>{field.label}</Label>
          <Input
            id={`edit-cred-${field.key}`}
            type={field.type}
            {...register(`credentials.${field.key}` as const)}
          />
        </div>
      ))}

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? 'Saving...' : 'Save Changes'}
      </Button>
    </form>
  );
}
